import React, { useState } from 'react';
import { FaPalette, FaCheck, FaRedo } from 'react-icons/fa';

const FlooringPatterns = ({ onPatternSelect, selectedPattern, selectedShapeId, updateShape, shapes, mergedStructures }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [patternAngle, setPatternAngle] = useState(0); // Pattern angle in degrees

  const patterns = [
    // Solid Colors
    { id: 'solid-white', name: 'White', type: 'solid', color: 0xFFFFFF, pattern: null },
    { id: 'solid-gray', name: 'Gray', type: 'solid', color: 0x808080, pattern: null },
    { id: 'solid-beige', name: 'Beige', type: 'solid', color: 0xF5F5DC, pattern: null },
    { id: 'solid-brown', name: 'Brown', type: 'solid', color: 0x8B4513, pattern: null },
    { id: 'solid-blue', name: 'Blue', type: 'solid', color: 0x87CEEB, pattern: null },
    { id: 'solid-green', name: 'Green', type: 'solid', color: 0x90EE90, pattern: null },
    
    // Wood Patterns
    { id: 'wood-oak', name: 'Oak Wood', type: 'wood', color: 0xD2691E, pattern: 'oak' },
    { id: 'wood-maple', name: 'Maple Wood', type: 'wood', color: 0xF4A460, pattern: 'maple' },
    { id: 'wood-walnut', name: 'Walnut Wood', type: 'wood', color: 0x654321, pattern: 'walnut' },
    { id: 'wood-cherry', name: 'Cherry Wood', type: 'wood', color: 0x8B0000, pattern: 'cherry' },
    
    // Tile Patterns
    { id: 'tile-ceramic', name: 'Ceramic Tile', type: 'tile', color: 0xF0F8FF, pattern: 'ceramic' },
    { id: 'tile-marble', name: 'Marble Tile', type: 'tile', color: 0xF5F5F5, pattern: 'marble' },
    { id: 'tile-granite', name: 'Granite Tile', type: 'tile', color: 0x696969, pattern: 'granite' },
    { id: 'tile-mosaic', name: 'Mosaic Tile', type: 'tile', color: 0x4169E1, pattern: 'mosaic' },
    
    // Carpet Patterns
    { id: 'carpet-plain', name: 'Plain Carpet', type: 'carpet', color: 0xDEB887, pattern: 'plain' },
    { id: 'carpet-textured', name: 'Textured Carpet', type: 'carpet', color: 0xCD853F, pattern: 'textured' },
    { id: 'carpet-patterned', name: 'Patterned Carpet', type: 'carpet', color: 0xDDA0DD, pattern: 'patterned' },
    
    // Concrete/Stone
    { id: 'concrete', name: 'Concrete', type: 'concrete', color: 0xC0C0C0, pattern: 'concrete' },
    { id: 'stone', name: 'Stone', type: 'stone', color: 0x708090, pattern: 'stone' },
  ];

  const handlePatternSelect = (pattern) => {
    const patternWithAngle = { ...pattern, angle: patternAngle };
    onPatternSelect(patternWithAngle);
    
    // Check if selectedShapeId is a merged structure
    const isMergedStructure = mergedStructures && mergedStructures.some(s => s.id === selectedShapeId);
    
    if (selectedShapeId && updateShape) {
      if (isMergedStructure) {
        // For merged structures, we need to handle this differently
        // We'll pass the pattern to the parent component to handle merged structure patterns
        const event = new CustomEvent('applyPatternToMergedStructure', {
          detail: {
            structureId: selectedShapeId,
            pattern: patternWithAngle
          }
        });
        window.dispatchEvent(event);
        console.log('✅ Applied flooring pattern to merged structure:', patternWithAngle.name, 'to structure:', selectedShapeId);
      } else {
        // For individual shapes, use the existing logic
        const currentShape = shapes?.find(s => s.id === selectedShapeId);
        if (currentShape) {
          updateShape(selectedShapeId, { 
            ...currentShape, // Preserve all existing properties
            flooringPattern: patternWithAngle 
          });
          console.log('✅ Applied flooring pattern:', patternWithAngle.name, 'to shape:', selectedShapeId);
        } else {
          updateShape(selectedShapeId, { flooringPattern: patternWithAngle });
          console.log('✅ Applied flooring pattern:', patternWithAngle.name, 'to shape:', selectedShapeId);
        }
      }
    }
    
    setIsOpen(false);
  };

  const getPatternPreview = (pattern) => {
    const size = 40;
    const style = {
      width: size,
      height: size,
      border: '2px solid #333',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      position: 'relative',
      background: `#${pattern.color.toString(16).padStart(6, '0')}`,
    };

    // Add pattern overlay based on type with angle support
    if (pattern.pattern) {
      const angle = patternAngle || 0;
      switch (pattern.pattern) {
        case 'oak':
        case 'maple':
        case 'walnut':
        case 'cherry':
          style.backgroundImage = `repeating-linear-gradient(
            ${angle}deg,
            transparent,
            transparent 4px,
            rgba(0,0,0,0.1) 4px,
            rgba(0,0,0,0.1) 6px
          )`;
          break;
        case 'ceramic':
        case 'marble':
        case 'granite':
          style.backgroundImage = `repeating-linear-gradient(
            ${angle}deg,
            transparent,
            transparent 8px,
            rgba(0,0,0,0.05) 8px,
            rgba(0,0,0,0.05) 10px
          ), repeating-linear-gradient(
            ${angle + 90}deg,
            transparent,
            transparent 8px,
            rgba(0,0,0,0.05) 8px,
            rgba(0,0,0,0.05) 10px
          )`;
          break;
        case 'mosaic':
          style.backgroundImage = `radial-gradient(
            circle at 25% 25%,
            rgba(255,255,255,0.3) 2px,
            transparent 2px
          ), radial-gradient(
            circle at 75% 75%,
            rgba(255,255,255,0.3) 2px,
            transparent 2px
          )`;
          break;
        case 'textured':
          style.backgroundImage = `repeating-linear-gradient(
            ${angle}deg,
            transparent,
            transparent 3px,
            rgba(0,0,0,0.1) 3px,
            rgba(0,0,0,0.1) 4px
          )`;
          break;
        case 'patterned':
          style.backgroundImage = `radial-gradient(
            circle at 50% 50%,
            rgba(255,255,255,0.2) 3px,
            transparent 3px
          )`;
          break;
        case 'concrete':
          style.backgroundImage = `repeating-linear-gradient(
            ${angle}deg,
            transparent,
            transparent 2px,
            rgba(0,0,0,0.1) 2px,
            rgba(0,0,0,0.1) 3px
          )`;
          break;
        case 'stone':
          style.backgroundImage = `radial-gradient(
            ellipse at 30% 30%,
            rgba(255,255,255,0.1) 5px,
            transparent 5px
          ), radial-gradient(
            ellipse at 70% 70%,
            rgba(255,255,255,0.1) 5px,
            transparent 5px
          )`;
          break;
      }
    }

    return style;
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '8px 12px',
          border: 'none',
          borderRadius: '6px',
          background: selectedPattern 
            ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)' 
            : 'linear-gradient(135deg, #1a1a1a 0%, #404040 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '600',
          width: '100%',
          justifyContent: 'flex-start'
        }}
      >
        <FaPalette />
        Flooring
        {selectedPattern && (
          <span style={{ fontSize: '12px', opacity: 0.8 }}>
            ({selectedPattern.name})
          </span>
        )}
      </button>

      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: '160px', // Fixed position from top of viewport
            right: '420px', // Fixed position from right of viewport (outside toolbar)
            background: 'linear-gradient(135deg, #1a1a1a 0%, #404040 100%)',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '16px',
            zIndex: 2500,
            minWidth: '320px',
            maxHeight: '500px',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}
        >
          <div style={{ marginBottom: '12px', fontWeight: 'bold', color: 'white' }}>
            Select Flooring Pattern
            {selectedShapeId && (
              <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
                Will apply to selected room
              </div>
            )}
          </div>

          {/* Pattern Angle Adjustment */}
          <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FaRedo style={{ color: '#4CAF50' }} />
              <span style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>Pattern Angle</span>
              <span style={{ color: '#4CAF50', fontSize: '12px', marginLeft: 'auto' }}>{patternAngle}°</span>
            </div>
            <input
              type="range"
              min="0"
              max="360"
              value={patternAngle}
              onChange={(e) => setPatternAngle(Number(e.target.value))}
              style={{
                width: '100%',
                height: '4px',
                background: 'linear-gradient(to right, #333 0%, #4CAF50 50%, #333 100%)',
                borderRadius: '2px',
                outline: 'none',
                WebkitAppearance: 'none',
                cursor: 'pointer'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#999', marginTop: '4px' }}>
              <span>0°</span>
              <span>90°</span>
              <span>180°</span>
              <span>270°</span>
              <span>360°</span>
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
            {patterns.map((pattern) => (
              <div
                key={pattern.id}
                onClick={() => handlePatternSelect(pattern)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  background: selectedPattern?.id === pattern.id ? 'rgba(255,255,255,0.2)' : 'transparent',
                  transition: 'background 0.2s ease'
                }}
              >
                <div style={getPatternPreview(pattern)}>
                  {selectedPattern?.id === pattern.id && (
                    <FaCheck style={{ color: 'white', fontSize: '16px' }} />
                  )}
                </div>
                <div style={{ fontSize: '11px', color: 'white', textAlign: 'center' }}>
                  {pattern.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlooringPatterns; 